/**
 * Theme palettes (dark mode, KAN-304 step 1) — the VALUES behind every `--cvx-<group>-<key>` custom
 * property that `semanticTokens.js` / `componentTokens.js` point at.
 *
 * LIGHT is derived from the literal tokens (never retyped), so it is today's app by construction
 * (plus the two founder-approved contrast fixes that live in `semanticLiteral.js`: `fg.icon`,
 * `border.field`). GRAPHITE is the founder-approved dark set (Palette.dc mockup, 2026-10-29): near-black
 * neutrals with NO blue cast, black-based shadows, no glow. A key the mockup did not cover is marked
 * `// chosen` — a neutral graphite equivalent picked in step 1.
 *
 * Both maps MUST have identical key sets (`themePalettes.test.js`).
 */

import { semanticLiteral } from './semanticLiteral';
import { GRAPHITE_EXTENDED } from './themePalettes.extended';
import { COLOR_GROUPS, themeVarName } from './semanticTokens';
import { COMPONENT_COLORS_LIGHT, componentVarName } from './componentTokens';

const flatten = (groups) => {
  const out = {};
  for (const [group, values] of Object.entries(groups)) {
    for (const [key, value] of Object.entries(values)) {
      if (value !== 'none') out[group === 'component' ? componentVarName(key) : themeVarName(group, key)] = value;
    }
  }
  return out;
};

const lightGroups = { component: COMPONENT_COLORS_LIGHT };
COLOR_GROUPS.forEach((g) => { lightGroups[g] = semanticLiteral[g]; });

export const LIGHT = flatten(lightGroups);

// Hue tints used by the role / status soft fills and borders (Graphite).
const tint = (rgb, a) => `rgba(${rgb}, ${a})`;
const GREEN = '48, 209, 88'; // Apple systemGreen (dark)
const AMBER = '255, 159, 10'; // Apple systemOrange (dark)
const RED = '255, 69, 58'; // Apple systemRed (dark)
const BLUE = '130, 178, 232';
const VIOLET = '196, 181, 253'; // chosen
const TEAL = '94, 234, 212'; // chosen
const INDIGO = '165, 180, 252';
const PINK = '249, 168, 212'; // chosen
const SKY = '125, 211, 252'; // chosen
const NAVY = '176, 204, 240';

const GRAPHITE_GROUPS = {
  bg: {
    canvas: '#000000',
    surface: '#1C1C1E',
    surfaceMuted: '#242426',
    frame: '#141416', // chosen: a hair above the black page, like a macOS sidebar; #242426 read as a grey slab around the content
    pageMuted: '#000000', // chosen: full pages sit on the same black as every other page; #242426 read as a grey slab
    surfaceElevated: '#2C2C2E',
    inverse: '#2C2C2E', // chosen: the "solid dark" bulk pill, raised so it reads on the canvas
    wash: '#050505', // mockup --wash
    tintHover: 'rgba(255, 255, 255, 0.06)', // chosen: white at the same weight as Light's ink wash
    tintTrack: 'rgba(255, 255, 255, 0.08)', // chosen
    scrollThumb: 'rgba(255, 255, 255, 0.28)', // chosen
    scrollThumbHover: 'rgba(255, 255, 255, 0.40)', // chosen
    scrollbar: 'rgba(146, 151, 159, 0.30)', // chosen
    scrollbarHover: 'rgba(146, 151, 159, 0.50)', // chosen
    scrollbarActive: 'rgba(146, 151, 159, 0.65)', // chosen
    scrollbarDrag: 'rgba(180, 184, 191, 0.75)', // chosen
    inverseGlass: 'rgba(44, 44, 46, 0.94)', // chosen: the glass pill one step above the surface
    inkFillHover: '#2B6CBB', // chosen: one step darker blue on hover, keeps the white label above AA (5.4:1)
    inkFill: '#2F72C4', // chosen: on Graphite the solid primary button is the brand blue fill (same as brand.primary), not an off-white slab (founder, 2026-10-30)
    onFillOverlayStrong: 'rgba(0, 0, 0, 0.2)', // chosen: running-fill sweep under a white label (6.9:1)
    onFillOverlay: 'rgba(0, 0, 0, 0.18)', // chosen: white label on the brand fill + this = 6.6:1 (a white overlay would drop it to 3.4:1)
  },
  fg: {
    default: '#F5F5F7',
    secondary: '#AEAEB2', // Apple neutral grey (colour system 2026-10-30)
    muted: '#98989D', // 4.8:1 even inside a #2C2C2E pop-up
    ink2: '#D1D1D6', // chosen: between default and secondary
    subtle: '#98989D',
    icon: '#8E8E93',
    placeholder: '#7C7C80', // 3:1 even inside a #2C2C2E pop-up
    faint: '#98989D', // quiet text that must still read: 5.3:1 on the #242426 fill, 4.8:1 on raised
    disabled: '#48484A', // chosen: disabled is exempt from contrast
    inverse: '#ffffff', // light text that sits on `bg.inverse` OR a brand fill
    inverseSoft: '#F5F5F7', // chosen: text on bg.inverseGlass
    onInkFill: '#FFFFFF', // chosen: white label on the blue fill (4.9:1)
    onPrimary: '#ffffff',
  },
  border: {
    default: '#3A3A3C',
    subtle: '#38383A',
    strong: '#6E6E73',
    focus: '#4A8DD4', // chosen: accent-400, 4.9:1 on the surface
    field: '#3A3A3C', // founder 2026-10-30 (KAN-1147): the same soft edge as the filter pills; focus = the blue ring
  },
  brand: {
    primary: '#2F72C4',
    primaryHover: '#2B6CBB',
    primaryMuted: 'rgba(74, 141, 212, 0.18)',
    primaryMutedStrong: 'rgba(74, 141, 212, 0.30)', // chosen
    primaryMutedBorder: 'rgba(74, 141, 212, 0.35)', // chosen
    text: '#82B2E8',
    onPrimary: '#ffffff',
    stop: '#FF6A3D',
    stopStrong: '#C8461F', // unchanged: it sits behind white text
    navy: '#B0CCF0', // mockup --navy: the nav glyph body
    fillEnd: '#245A9E', // chosen: gradient end behind white text (7.1:1)
    routeLight: '#2D5283', // chosen: mockup d200, the dim start of a drawn route
    logoVex: '#82B2E8', // mockup --logoVex (7:1 on the surface)
  },
  status: {
    success: '#30D158',
    successIcon: '#30D158', // Apple systemGreen
    finishSave: '#1A7F53', // chosen: same deep green as successFill; mint `success` is a TEXT green and washes out behind white
    finishNeutral: '#636366', // chosen: Apple systemGray, white label 6:1; fg.secondary is a TEXT grey and read pale/pink as a fill
    successFill: '#1A7F53', // chosen: deep green so the white label reads (5:1); the mint icon green is too light behind white
    successSoft: tint(GREEN, 0.14),
    successBorder: tint(GREEN, 0.35), // chosen
    successFaint: tint(GREEN, 0.08), // chosen
    successFaintBorder: tint(GREEN, 0.18), // chosen
    successRing: tint(GREEN, 0.3), // chosen
    warning: '#FF9F0A',
    warningFill: '#B45309', // chosen: deep amber so the white label reads (5.0:1); the amber TEXT colour is too light behind white
    warningSoft: tint(AMBER, 0.14),
    warningBorder: tint(AMBER, 0.35), // chosen
    warningFaint: tint(AMBER, 0.08), // chosen
    warningFaintBorder: tint(AMBER, 0.2), // chosen
    warningStrong: '#FF9F0A', // chosen
    warningText: '#FF9F0A',
    danger: '#FF6961', // Apple's readable red: 5.7:1 even on its own soft chip
    dangerSoft: tint(RED, 0.14),
    dangerStrong: '#FF6961', // chosen
    dangerFill: '#DC2626', // chosen: white on it 4.8:1 (white on #F87171 is only 2.8:1)
    dangerFaint: '#261A1B',
    dangerBorder: tint(RED, 0.35),
    dangerOutline: tint(RED, 0.45), // chosen
    info: '#82B2E8',
    infoFill: '#2F72C4', // chosen: brand blue fill, white label 4.9:1; the pale info TEXT blue washes out behind white
    infoSoft: tint(BLUE, 0.14),
    infoBorder: tint(BLUE, 0.35), // chosen
    violet: '#C4B5FD', // chosen
    violetSoft: tint(VIOLET, 0.14), // chosen
    violetBorder: tint(VIOLET, 0.35), // chosen
    operations: '#5EEAD4', // chosen
    operationsSoft: tint(TEAL, 0.14), // chosen
    operationsBorder: tint(TEAL, 0.35), // chosen
    successWash: tint(GREEN, 0.10), // chosen
    successWashRing: tint(GREEN, 0.22), // chosen
    warningWash: tint(AMBER, 0.10), // chosen
    warningWashRing: tint(AMBER, 0.22), // chosen
  },
  role: {
    owner: '#F5F5F7',
    admin: '#A5B4FC',
    supervisor: '#C4B5FD', // chosen
    operations: '#5EEAD4', // chosen
    billing: '#F9A8D4', // chosen
    worker: '#7DD3FC', // chosen
    platformRole: '#B0CCF0',
  },
  roleSoft: {
    owner: 'rgba(245, 245, 247, 0.08)', // chosen
    admin: tint(INDIGO, 0.14), // chosen
    supervisor: tint(VIOLET, 0.14), // chosen
    operations: tint(TEAL, 0.14), // chosen
    billing: tint(PINK, 0.14), // chosen
    worker: tint(SKY, 0.14), // chosen
    platformRole: tint(NAVY, 0.14), // chosen
  },
  roleBorder: {
    owner: 'rgba(245, 245, 247, 0.18)', // chosen
    admin: tint(INDIGO, 0.3), // chosen
    supervisor: tint(VIOLET, 0.3), // chosen
    operations: tint(TEAL, 0.3), // chosen
    billing: tint(PINK, 0.3), // chosen
    worker: tint(SKY, 0.3), // chosen
    platformRole: tint(NAVY, 0.3), // chosen
  },
  elevation: {
    xs: '0 1px 2px rgba(0, 0, 0, 0.4)', // chosen
    sm: '0 1px 3px rgba(0, 0, 0, 0.4)', // chosen
    md: '0 4px 12px rgba(0, 0, 0, 0.45)', // chosen
    widget: '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.35)',
    widgetHover: '0 2px 4px rgba(0, 0, 0, 0.45), 0 12px 32px rgba(0, 0, 0, 0.45)', // chosen
    elevated: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 12px 32px rgba(0, 0, 0, 0.6)', // chosen: hairline edge + depth (no glow), Apple-style
    floating: '0 0 0 1px rgba(255, 255, 255, 0.08), 0 2px 6px rgba(0, 0, 0, 0.45), 0 12px 32px rgba(0, 0, 0, 0.6)', // chosen: hairline edge on every floating layer
    focusRing: '0 0 0 2px #1C1C1E, 0 0 0 4px #4A8DD4', // chosen: solid ring, 4.9:1
    fieldFocusRing: '0 0 0 2px rgba(74, 141, 212, 0.3)',
    fieldFocusRingDanger: `0 0 0 2px ${tint(RED, 0.25)}`, // chosen
    toast: '0 0 0 1px rgba(255,255,255,0.08), 0 18px 40px -8px rgba(0,0,0,0.6), 0 4px 12px rgba(0,0,0,0.4)', // chosen: hairline edge, no glow
    toastHalo: '0 0 0 4px rgba(255,255,255,0.04)', // chosen: a hairline ring
    fieldInset: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)', // chosen: no white lip on dark
  },
  nav: {
    bar: '#0A0A0A', // mockup --navBar
    barEdge: 'rgba(255, 255, 255, 0.06)',
    barHighlight: 'rgba(255, 255, 255, 0.06)', // chosen
    barShadow: '0 0 0 0.5px rgba(0, 0, 0, 0.5), 0 2px 6px rgba(0, 0, 0, 0.4), 0 12px 28px -8px rgba(0, 0, 0, 0.6)', // chosen
    selectedCard: '#2C2C2E',
    selectedHighlight: 'rgba(255, 255, 255, 0.06)',
    selectedShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.06), 0 0 0 0.5px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3), 0 4px 10px -4px rgba(0, 0, 0, 0.5)',
    segSelectedCard: '#48484A', // one step lighter than the nav card so the pick separates from the track
    segSelectedShadow: 'inset 0 0 0 0.5px rgba(255, 255, 255, 0.16), 0 1px 3px rgba(0, 0, 0, 0.6)', // hairline edge
    icon: '#98989D',
    iconSelected: '#F5F5F7',
    track: '#000000',
    headerEdge: '#38383A',
  },
  dataRamp: {
    900: '#4A8DD4', // approved dark brand data blue
    600: '#3E78B8',
    400: '#335E95',
    200: '#2D5283',
    track: '#38383A',
  },
  homeEdge: {
    card: '#3A3A3C', // chosen: a hair above the hairline
    hairline: '#38383A',
    button: '#48484A', // chosen
  },
  legacy: {
    bg: '#000000',
    surface: '#1C1C1E',
    surfaceAlt: '#242426',
    surfaceMuted: '#242426',
    primary: '#2F72C4',
    primaryDark: '#2B6CBB',
    primaryLight: '#2D5283',
    primarySoft: 'rgba(74, 141, 212, 0.16)',
    secondary: '#98989D',
    secondaryLight: '#6E6E73',
    secondarySoft: '#242426',
    accent: '#2F72C4',
    accentSoft: 'rgba(74, 141, 212, 0.16)',
    border: '#3A3A3C',
    borderLight: '#38383A',
    text: '#F5F5F7',
    textMuted: '#98989D',
    textLight: '#8E8E93',
    success: '#30D158',
    successLight: tint(GREEN, 0.35),
    successSoft: tint(GREEN, 0.14),
    warning: '#FF9F0A',
    warningText: '#FF9F0A',
    warningLight: tint(AMBER, 0.35),
    warningSoft: tint(AMBER, 0.14),
    danger: '#FF6961',
    dangerLight: tint(RED, 0.35),
    dangerSoft: tint(RED, 0.14),
    purple: '#98989D',
    purpleSoft: '#242426',
    menuTint: '#242426',
  }, // all chosen: each legacy key maps to its Graphite role
  groupedList: {
    tint: '#242426', // chosen
    tintFaint: '#202022', // chosen
    ink: '#8FA8CC', // chosen: 6:1 on the tint
    monthGlass: 'rgba(0, 0, 0, 0.94)', // chosen: canvas as glass
    divider: '#38383A',
    rowHover: '#242426',
  },
  component: {
    cardEdge: 'rgba(255, 255, 255, 0.13)', // chosen
    scrim: 'rgba(0, 0, 0, 0.66)',
    scrimSoft: 'rgba(0, 0, 0, 0.5)', // chosen
    scrimMenu: 'rgba(0, 0, 0, 0.45)', // chosen
    scrimInk: '#000000', // chosen: base ink for alpha-driven scrims
  },
};

export const GRAPHITE = flatten({ ...GRAPHITE_GROUPS, ...GRAPHITE_EXTENDED });
